import { useState } from "react";
import { CalendarDays } from "lucide-react";
import { PopupModal } from "react-calendly";

import { Button } from "@/components/ui/button";
import { WhatsappIcon } from "@/components/common/whatsapp-brand";
import { authClient } from "@/lib/auth-client";
import { capture } from "@/lib/posthog";
import { salesWhatsappLink } from "@/lib/whatsapp";
import { cn } from "@/lib/utils";
import {
  SALES_ADVISOR_NAME,
  SALES_ADVISOR_PHOTO_URL,
  SALES_CALENDLY_URL,
} from "@/constants";

const WHATSAPP_LINK = salesWhatsappLink(
  "Hola, quiero ayuda para configurar mis líneas de negocio en Licitigo.",
);

/**
 * Retrato del asesor en un círculo. La foto es de medio cuerpo: se amplía y se
 * desplaza para que quede encuadrado el rostro, no el saco.
 */
function AdvisorAvatar({ className }: { className: string }) {
  return (
    <span
      className={cn(
        "relative shrink-0 overflow-hidden rounded-full bg-primary/10",
        className,
      )}
    >
      <img
        src={SALES_ADVISOR_PHOTO_URL}
        alt={`${SALES_ADVISOR_NAME}, asesor de Licitigo`}
        loading="lazy"
        className="absolute left-1/2 top-0 w-[230%] max-w-none -translate-x-[54%] -translate-y-[10%]"
      />
    </span>
  );
}

/**
 * Invita a agendar una reunión de configuración con el equipo, con WhatsApp como
 * salida rápida. Vive bajo la barra de líneas de negocio del Descubrimiento,
 * solo en desktop.
 */
export function ScheduleMeetingCard() {
  const { data: session } = authClient.useSession();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative overflow-hidden rounded-2xl bg-card p-5 shadow-sm">
      <div className="pointer-events-none absolute -right-8 -top-8 size-24 rounded-full bg-primary/10" />

      <div className="relative flex flex-col items-start gap-3">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-primary">
          <span className="size-1.5 rounded-full bg-primary" />
          30 min · Gratis
        </span>

        <div className="flex items-center gap-3">
          <AdvisorAvatar className="size-11" />

          <div>
            <p className="text-base font-bold tracking-tight">
              Reúnete con nosotros
            </p>
            <p className="text-xs text-muted-foreground">
              Con {SALES_ADVISOR_NAME}, tu asesor
            </p>
          </div>
        </div>

        <p className="-mt-1 text-sm leading-relaxed text-muted-foreground">
          Te ayudamos a configurar tus líneas de negocio para que no se te escape
          ninguna licitación.
        </p>

        <div className="flex w-full flex-col gap-2">
          <Button
            className="w-full font-bold"
            onClick={() => {
              capture("schedule_meeting_clicked");
              setOpen(true);
            }}
          >
            <CalendarDays className="size-4" />
            Agendar reunión
          </Button>

          {/* Segunda opción, no segunda acción: va en outline para no competir
              con el CTA azul; el verde queda solo en el glifo. */}
          <Button variant="outline" className="w-full font-bold" asChild>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                capture("sales_whatsapp_clicked", { source: "advisor_card" })
              }
            >
              <WhatsappIcon className="size-4 text-[#25D366]" />
              Háblanos por WhatsApp
            </a>
          </Button>
        </div>
      </div>

      {/* El modal se monta al abrir: así el iframe de Calendly solo se carga
          cuando alguien lo pide. */}
      {open && (
        <PopupModal
          url={SALES_CALENDLY_URL}
          open
          onModalClose={() => setOpen(false)}
          rootElement={document.body}
          prefill={{
            name: session?.user?.name ?? "",
            email: session?.user?.email ?? "",
          }}
        />
      )}
    </div>
  );
}

/**
 * Versión de una fila para móvil, donde la card completa se comía media
 * pantalla. Va bajo el selector de línea de negocio y sale directo a WhatsApp:
 * en el celular escribir es un tap, agendar son varios.
 */
export function ScheduleMeetingRow() {
  return (
    <a
      href={WHATSAPP_LINK}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => capture("sales_whatsapp_clicked", { source: "mobile_row" })}
      className="flex items-center gap-3 rounded-2xl bg-card p-3 shadow-sm active:bg-accent"
    >
      <AdvisorAvatar className="size-9" />

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold tracking-tight">
          ¿Te ayudo a afinar tus líneas?
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {SALES_ADVISOR_NAME}, tu asesor · Gratis
        </p>
      </div>

      <WhatsappIcon className="size-5 shrink-0 text-[#25D366]" />
    </a>
  );
}
